import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useSeo } from "../hooks/useSeo";

const IMG = "https://images.unsplash.com/photo-1459679749680-18eb1eb37418?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxhbXN0ZXJkYW0lMjBjYW5hbCUyMGhvdXNlcyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Nzk3MjY0Mjd8MA&ixlib=rb-4.1.0&q=85";

export default function ExpatHousing() {
  const { t, lang } = useI18n();
  useSeo({ title: t.seo.expat.title, description: t.seo.expat.description, path: "/expat-housing", lang, image: IMG });

  return (
    <>
      {/* Editorial hero */}
      <section className="container-wide pt-12 md:pt-20 pb-12 md:pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-12 flex items-center justify-between gap-6">
            <p className="eyebrow">{t.expat.eyebrow}</p>
            <p className="hidden md:block text-xs uppercase tracking-[0.22em] text-muted-foreground font-bold">{t.editorial.expatPracticeLabel}</p>
          </div>
          <h1 className="lg:col-span-12 text-[40px] leading-[0.98] tracking-[-0.025em] font-medium text-foreground sm:text-[60px] lg:text-[96px] xl:text-[112px]">
            {t.expat.title}
          </h1>
          <div className="lg:col-span-7">
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">{t.expat.lead}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact?intent=intake" className="btn-primary" data-testid="expat-cta">
                {t.expat.cta} <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/tarieven" className="btn-ghost">{t.home.seePricing}</Link>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="border-t border-border pt-6 space-y-4 max-w-md ml-auto text-sm">
              <div className="flex items-baseline justify-between gap-6"><span className="font-display text-2xl text-foreground">2–4</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.expatStat1Label}</span></div>
              <div className="flex items-baseline justify-between gap-6 border-t border-border pt-4"><span className="font-display text-2xl text-foreground">€250</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.pricingExpatRow1}</span></div>
              <div className="flex items-baseline justify-between gap-6 border-t border-border pt-4"><span className="font-display text-2xl text-foreground">3</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.expatStat3Label}</span></div>
            </div>
          </aside>
        </div>
      </section>

      {/* Wide editorial image */}
      <section className="container-wide pb-16 md:pb-24">
        <div className="relative overflow-hidden border-y border-border">
          <img src={IMG} alt="Amsterdam canal houses — expat housing" className="w-full h-[42vh] md:h-[56vh] object-cover" />
          <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] font-bold text-white/90 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-sm">
            Fig. 02 — Amsterdam, canal district
          </span>
        </div>
      </section>

      {/* Numbered editorial scope */}
      <section className="border-t border-border" data-testid="expat-points">
        <div className="container-wide py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow">{t.editorial.scopeEyebrow}</p>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
              {t.editorial.expatScopeTitleBefore}<span className="italic text-primary">{t.editorial.expatScopeHighlight}</span>{t.editorial.expatScopeTitleAfter}
            </h2>
          </div>
          <ol className="lg:col-span-8 border-y border-border">
            {t.expat.points.map((p, i) => (
              <li key={i} className="grid md:grid-cols-12 gap-6 py-7 md:py-8 border-b border-border last:border-b-0 items-baseline">
                <span className="md:col-span-1 font-display text-3xl text-accent leading-none">{String(i + 1).padStart(2, "0")}</span>
                <p className="md:col-span-11 text-lg md:text-xl text-foreground leading-snug">{p}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Quiet CTA strip */}
      <section className="border-t border-border bg-secondary/40">
        <div className="container-wide py-16 md:py-20 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <p className="font-display text-2xl md:text-3xl text-foreground tracking-[-0.015em] leading-snug">
              {t.editorial.expatCtaText} <span className="italic text-primary">{t.editorial.expatCtaHighlight}</span>
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact?intent=intake" className="btn-primary">{t.expat.cta} <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
